import React from 'react'

import { observer } from 'mobx-react'
import {
  ModalSheet,
  ModalSheetHeader,
  ModalSheetContentTitle,
  ModalSheetContentDescription,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetFooter,
  ModalSheetFooterSpacer,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSection,
  ModalSheetContentBox,
} from 'components/ModalSheet'

import styles from './OfferModal.sass'
import Button from 'components/Button'
import Icon from 'components/Icon'
import { OfferModalViewModel } from '../../OfferModalViewModel'
import { match } from 'utils/pattern-match-util'
import { OfferExpirationAction } from '../../OpportunityOffer'
import cx from 'classnames'
import { formattedTimeUntil } from 'utils/date-util'
import ErrorBlock from 'components/ErrorBlock'
import { Congrats } from '../../../components/Congrats'

interface Props {
  vm: OfferModalViewModel
}

/**
 * The Offer Modal.
 */
export default observer(function OfferModal({ vm }: Props) {
  const expirationActionText = match(vm.offerExpirationAction, {
    [OfferExpirationAction.Accept]: () => 'Auto-accepting',
    [OfferExpirationAction.Decline]: () => 'Auto-declining',
  })

  const clockStyles = cx(
    styles.clock,
    match(vm.offerExpirationAction, {
      [OfferExpirationAction.Accept]: () => styles.clockAutoAccept,
      [OfferExpirationAction.Decline]: () => styles.clockAutoDecline,
    }),
  )

  const countdownStyles = cx(
    styles.countdown,
    match(vm.offerExpirationAction, {
      [OfferExpirationAction.Accept]: () => styles.countdownAutoAccept,
      [OfferExpirationAction.Decline]: () => styles.countdownAutoDecline,
    }),
  )

  return (
    <ModalSheet
      showing={vm.showing}
      disableCloseOnEsc
      disableCloseOnOutsideClick
    >
      <ModalSheetHeader />
      <ModalSheetContent leftAligned>
        <ModalSheetContentBlock>
          <Congrats />
          <ModalSheetContentTitle>
            You scored a job on {vm.workspaceName}
          </ModalSheetContentTitle>
          <ModalSheetContentDescription>
            To claim, review and accept the opportunity below.
          </ModalSheetContentDescription>
          <ModalSheetContentBox
            data-test={`offer-modal-${vm.id}`}
            color="green"
          >
            <span
              className={styles.opportunityTitle}
              data-test={`offer-modal-display-name-${vm.id}`}
            >
              {vm.displayName}
            </span>
            <div className={styles.clientAndProCut}>
              <span>{vm.championClientName}</span>
              {vm.proCut && (
                <>
                  <span className={styles.separator}>•</span>
                  <span>{vm.proCut}</span>
                </>
              )}
            </div>
            <p className={styles.specs}>{vm.jobSpecs.join(' • ')}</p>
            <div className={styles.offerAction}>
              <Button
                onClick={vm.acceptOpportunity}
                loading={vm.acceptOpportunity.pending}
                block
                dataTest={`offer-modal-accept-button-${vm.id}`}
                type="success"
                size="cta"
              >
                Accept Opportunity
              </Button>
            </div>
          </ModalSheetContentBox>
        </ModalSheetContentBlock>
      </ModalSheetContent>

      {vm.acceptOpportunity.rejected && (
        <ErrorBlock
          tackOn="bottom"
          error={vm.acceptOpportunity.error}
          onCloseClick={() => vm.acceptOpportunity.reset()}
        />
      )}

      {vm.declineOpportunity.rejected && (
        <ErrorBlock
          tackOn="bottom"
          error={vm.declineOpportunity.error}
          onCloseClick={() => vm.declineOpportunity.reset()}
        />
      )}

      <ModalSheetFooter>
        <ModalSheetFooterSection>
          <div className={styles.countdownDisplay}>
            <Icon className={clockStyles} name="clock" auto />
            {expirationActionText} in{' '}
            <span className={countdownStyles}>
              {formattedTimeUntil(vm.timeUntilExpiration!)}
            </span>
          </div>
        </ModalSheetFooterSection>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            onClick={vm.declineOpportunity}
            loading={vm.declineOpportunity.pending}
            dataTest={`offer-modal-decline-button-${vm.id}`}
          >
            Decline Opportunity
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})
